import { serializeCoachSection, type EditableCoach } from './kaiCoachEditorModel';

// Building the UpdateCourse payload by preserving the loaded component and only
// swapping its kai_coach_section is the whole point: the backend replaces the
// stored component wholesale, so any field the request omits (is_live, the
// content/homework start timestamps that control visibility, etc.) would be
// reset. Cloning the base component keeps all of that intact across coach edits.

function cloneDeep<T>(value: T): T {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value)) as T;
}

/**
 * Produce the component to send to UpdateCourse: a deep clone of the loaded
 * component with its Kai Coach section's payload replaced by the edited one.
 * Every other field (visibility flag, timestamps, title, uid, ...) is preserved.
 */
export function buildCoachUpdateComponent(
  baseComponent: unknown,
  coach: EditableCoach,
  coachUid: string,
): Record<string, unknown> {
  const component =
    baseComponent && typeof baseComponent === 'object'
      ? (cloneDeep(baseComponent) as Record<string, unknown>)
      : {};

  const sections = Array.isArray(component.sections)
    ? (component.sections as Array<Record<string, unknown>>)
    : [];

  let target = sections.find(
    (section) =>
      section && (section.kai_coach_section ?? section.kaiCoachSection),
  );

  if (!target) {
    target = {};
    sections.push(target);
  }

  target.kai_coach_section = serializeCoachSection(coach, coachUid);
  // Avoid sending both spellings of the same field.
  delete target.kaiCoachSection;

  component.sections = sections;

  // The title lives on the component, not in the coach section. A blank one
  // keeps whatever is stored: the editor refuses to save it, and nothing else
  // may erase a title by accident.
  const title = coach.title.trim();
  if (title) {
    component.title = title;
  }

  return component;
}

export function buildCoachUpdatePayload(
  courseId: string,
  baseComponent: unknown,
  coach: EditableCoach,
  coachUid: string,
): { components: Array<Record<string, unknown>>; id: string } {
  return {
    components: [buildCoachUpdateComponent(baseComponent, coach, coachUid)],
    id: courseId,
  };
}
